import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Send, Plus, Trash2, Loader2 } from "lucide-react";

// ── Types ────────────────────────────────────────────────────

type Method = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

interface Header {
    id: number;
    key: string;
    value: string;
}

interface ApiResponse {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body: string;
    time: number;
}

// ── Component ────────────────────────────────────────────────

export function ApiTester() {
    const [method, setMethod] = useState<Method>("GET");
    const [url, setUrl] = useState("https://jsonplaceholder.typicode.com/posts/1");
    const [headers, setHeaders] = useState<Header[]>([{ id: 1, key: "Content-Type", value: "application/json" }]);
    const [body, setBody] = useState('{\n  "title": "test",\n  "body": "hello world",\n  "userId": 1\n}');
    const [response, setResponse] = useState<ApiResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState<string | null>(null);
    const [nextHeaderId, setNextHeaderId] = useState(2);

    useEffect(() => {
        document.title = "API Tester — Send HTTP Requests Online — Ballah Code";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute(
                "content",
                "Free online API tester. Send GET, POST, PUT, DELETE, and PATCH requests. Set custom headers and JSON body. See response status, headers, and body.",
            );
        }
    }, []);

    const sendRequest = async () => {
        if (!url.trim()) return;
        setError(null);
        setResponse(null);
        setLoading(true);

        const start = performance.now();
        try {
            const reqHeaders: Record<string, string> = {};
            headers.forEach((h) => {
                if (h.key.trim()) reqHeaders[h.key.trim()] = h.value;
            });

            const init: RequestInit = {
                method,
                headers: reqHeaders,
            };

            if (["POST", "PUT", "PATCH"].includes(method) && body.trim()) {
                init.body = body;
            }

            const res = await fetch(url, init);
            const elapsed = performance.now() - start;

            const resHeaders: Record<string, string> = {};
            res.headers.forEach((val, key) => {
                resHeaders[key] = val;
            });

            let resBody: string;
            const contentType = res.headers.get("content-type") || "";
            if (contentType.includes("json")) {
                const json = await res.json();
                resBody = JSON.stringify(json, null, 2);
            } else {
                resBody = await res.text();
            }

            setResponse({
                status: res.status,
                statusText: res.statusText,
                headers: resHeaders,
                body: resBody,
                time: Math.round(elapsed),
            });
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Request failed");
        } finally {
            setLoading(false);
        }
    };

    const addHeader = () => {
        setHeaders((prev) => [...prev, { id: nextHeaderId, key: "", value: "" }]);
        setNextHeaderId((n) => n + 1);
    };

    const removeHeader = (id: number) => {
        setHeaders((prev) => prev.filter((h) => h.id !== id));
    };

    const updateHeader = (id: number, field: "key" | "value", val: string) => {
        setHeaders((prev) => prev.map((h) => (h.id === id ? { ...h, [field]: val } : h)));
    };

    const copy = async (val: string, key: string) => {
        await navigator.clipboard.writeText(val);
        setCopied(key);
        setTimeout(() => setCopied(null), 2000);
    };

    const statusColor = (status: number) => {
        if (status < 300) return "text-green-400";
        if (status < 400) return "text-yellow-400";
        return "text-red-400";
    };

    const METHODS: Method[] = ["GET", "POST", "PUT", "DELETE", "PATCH"];
    const METHOD_COLORS: Record<Method, string> = {
        GET: "text-green-400",
        POST: "text-blue-400",
        PUT: "text-yellow-400",
        DELETE: "text-red-400",
        PATCH: "text-orange-400",
    };

    return (
        <div className="min-h-screen bg-background text-foreground font-sans">
            <nav className="flex items-center justify-between px-8 py-[18px]">
                <Link to="/" className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors">ballah code</Link>
                <div className="flex items-center gap-6 text-[13px] text-zinc-600">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">tools</Link>
                    <Link to="/" className="hover:text-zinc-400 transition-colors">home</Link>
                </div>
            </nav>

            <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
                <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">Tools</Link>
                    <span className="text-zinc-700">/</span>
                    <span className="text-zinc-400">API Tester</span>
                </div>

                <div className="mb-8">
                    <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">API Tester</h1>
                    <p className="text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
                        Send HTTP requests and inspect responses. Set method, URL, headers, and body. Like a lightweight Postman — right in your browser.
                    </p>
                </div>

                {/* URL bar */}
                <div className="flex items-center gap-2 mb-6">
                    <div className="flex rounded-lg border border-white/[0.06] overflow-hidden">
                        {METHODS.map((m) => (
                            <button
                                key={m}
                                onClick={() => setMethod(m)}
                                className={`px-2.5 py-2.5 text-[11px] font-mono font-semibold transition-colors ${method === m ? `bg-white/[0.08] ${METHOD_COLORS[m]}` : "text-zinc-600 hover:text-zinc-400"}`}
                            >
                                {m}
                            </button>
                        ))}
                    </div>

                    <div className="flex-1 flex items-center bg-[#111113] border border-white/[0.06] rounded-lg overflow-hidden">
                        <input
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://api.example.com/endpoint"
                            spellCheck={false}
                            onKeyDown={(e) => e.key === "Enter" && sendRequest()}
                            className="flex-1 bg-transparent px-4 py-2.5 text-[13px] text-zinc-300 font-mono outline-none placeholder:text-zinc-800"
                        />
                    </div>

                    <button
                        onClick={sendRequest}
                        disabled={loading}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-[13px] font-medium transition-colors disabled:opacity-50"
                    >
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                        Send
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                    {/* Headers */}
                    <div>
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">Headers</span>
                            <button onClick={addHeader} className="text-zinc-600 hover:text-zinc-300 transition-colors">
                                <Plus size={13} />
                            </button>
                        </div>
                        <div className="bg-[#111113] border border-white/[0.06] rounded-b-xl p-3 space-y-2 min-h-[120px]">
                            {headers.map((h) => (
                                <div key={h.id} className="flex items-center gap-2">
                                    <input
                                        value={h.key}
                                        onChange={(e) => updateHeader(h.id, "key", e.target.value)}
                                        placeholder="Key"
                                        className="flex-1 bg-white/[0.03] rounded px-2.5 py-1.5 text-[12px] text-zinc-300 font-mono outline-none placeholder:text-zinc-800"
                                    />
                                    <input
                                        value={h.value}
                                        onChange={(e) => updateHeader(h.id, "value", e.target.value)}
                                        placeholder="Value"
                                        className="flex-1 bg-white/[0.03] rounded px-2.5 py-1.5 text-[12px] text-zinc-300 font-mono outline-none placeholder:text-zinc-800"
                                    />
                                    <button onClick={() => removeHeader(h.id)} className="text-zinc-700 hover:text-red-400 transition-colors shrink-0">
                                        <Trash2 size={11} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Body */}
                    <div>
                        <div className="px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">
                                Body {!["POST", "PUT", "PATCH"].includes(method) && <span className="text-zinc-800 normal-case">(ignored for {method})</span>}
                            </span>
                        </div>
                        <textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            placeholder='{"key": "value"}'
                            spellCheck={false}
                            className={`w-full min-h-[120px] bg-[#111113] border border-white/[0.06] rounded-b-xl p-3 text-[12px] text-zinc-300 font-mono leading-relaxed outline-none resize-y placeholder:text-zinc-800 transition-colors ${["POST", "PUT", "PATCH"].includes(method) ? "focus:border-white/[0.1]" : "opacity-50"}`}
                        />
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="text-[13px] text-red-400 mb-4 p-3 bg-red-400/[0.05] border border-red-400/10 rounded-xl">{error}</div>
                )}

                {/* Response */}
                {response && (
                    <div className="mb-16">
                        {/* Status bar */}
                        <div className="flex items-center gap-4 px-4 py-3 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <span className={`text-[14px] font-mono font-semibold ${statusColor(response.status)}`}>
                                {response.status} {response.statusText}
                            </span>
                            <span className="text-[12px] text-zinc-600">{response.time}ms</span>
                            <span className="text-[12px] text-zinc-600">{new Blob([response.body]).size} bytes</span>
                            <button onClick={() => copy(response.body, "body")} className="flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-300 transition-colors ml-auto">
                                {copied === "body" ? <Check size={11} className="text-green-400" /> : <Copy size={11} />}
                                Copy
                            </button>
                        </div>

                        {/* Response headers */}
                        <details className="bg-[#0e0e10] border-x border-white/[0.06]">
                            <summary className="px-4 py-2 text-[11px] text-zinc-600 cursor-pointer hover:text-zinc-400 transition-colors">
                                Response Headers ({Object.keys(response.headers).length})
                            </summary>
                            <div className="px-4 pb-3 space-y-1">
                                {Object.entries(response.headers).map(([key, val]) => (
                                    <div key={key} className="text-[11px] font-mono">
                                        <span className="text-violet-400">{key}</span>
                                        <span className="text-zinc-700">: </span>
                                        <span className="text-zinc-400">{val}</span>
                                    </div>
                                ))}
                            </div>
                        </details>

                        {/* Response body */}
                        <div className="bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 max-h-[500px] overflow-auto">
                            <pre className="text-[12px] text-zinc-300 font-mono whitespace-pre-wrap break-all">{response.body}</pre>
                        </div>
                    </div>
                )}

                {/* SEO */}
                <section className="max-w-2xl border-t border-white/[0.04] pt-12">
                    <h2 className="text-lg font-medium tracking-[-0.02em] mb-4 text-zinc-300">About API testing</h2>
                    <div className="space-y-4 text-[14.5px] text-zinc-500 leading-[1.75]">
                        <p>API testing involves sending HTTP requests to an endpoint and inspecting the response. It's essential for debugging REST APIs, verifying webhooks, testing integrations, and prototyping frontend features before the backend is ready.</p>
                        <p>This tool supports GET, POST, PUT, DELETE, and PATCH methods with custom headers and JSON body. Responses show status codes, timing, headers, and the formatted response body. Note: requests are made from your browser, so CORS restrictions may apply for some APIs.</p>
                        <p>Built by the team behind <Link to="/" className="text-violet-400 hover:text-violet-300 transition-colors">Ballah Code</Link>.</p>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-2">
                        <span className="text-[12px] text-zinc-700 mr-1 self-center">Related →</span>
                        <Link to="/tools/json-formatter" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JSON Formatter</Link>
                        <Link to="/tools/jwt-encoder" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JWT Encoder</Link>
                        <Link to="/tools/url-encode-decode" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">URL Encode / Decode</Link>
                        <Link to="/tools/base64" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Base64 Encode / Decode</Link>
                    </div>
                </section>
            </main>

            <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
                <Link to="/" className="hover:text-zinc-600 transition-colors">ballah code</Link>
                <Link to="/tools" className="hover:text-zinc-600 transition-colors">tools</Link>
                <span>ballah tech</span>
            </footer>
        </div>
    );
}
